import type { Chain } from './constants';
import { BASE_URLS, DISABLED_SERVICES_BY_CHAIN, REDIRECT_EXCLUDED_SERVICES } from './constants';
import type { UserState } from './initial-states';
import {
  type ChainSlug,
  chainIdToSlug,
  buildChainUrl,
  stripChainFromPath,
  CHAIN_DEPENDENT_BASE_PATHS,
} from './chain-slug';

const getNavigableServices = (user: UserState['user'] | undefined, activeChain: Chain) => {
  const serviceDetails = user?.serviceDetails || [];
  const chainDisabledServices = DISABLED_SERVICES_BY_CHAIN[activeChain] || [];
  return serviceDetails.filter(
    (service) =>
      !service.external &&
      !REDIRECT_EXCLUDED_SERVICES.includes(service.id) &&
      !chainDisabledServices.includes(service.id)
  );
};

/**
 * Check if a base path is chain-dependent (needs chain prefix in URL).
 */
function isChainDependentBasePath(basePath: string): boolean {
  return (CHAIN_DEPENDENT_BASE_PATHS as readonly string[]).includes(basePath);
}

/**
 * Get the back to app URL for the active service, with chain prefix.
 *
 * If the active service is not navigable, get the URL for the first navigable service.
 */
export function getBackToAppUrl(
  activeService: string,
  activeChain: Chain,
  user: UserState['user']
) {
  const navigableServices = getNavigableServices(user, activeChain);
  const targetService =
    navigableServices.find((s) => s.id === activeService) || navigableServices[0];
  const basePath = targetService ? BASE_URLS[targetService.id as keyof typeof BASE_URLS] : null;

  if (!basePath) return null;

  // Add chain prefix for chain-dependent routes
  if (isChainDependentBasePath(basePath)) {
    const chainSlug = chainIdToSlug(activeChain);
    return buildChainUrl(basePath, chainSlug);
  }

  return basePath;
}

/**
 * Get the active service from the pathname.
 * Handles both chain-prefixed paths (e.g. /is/valikoimat) and plain paths.
 */
export function getActiveServiceFromPathname(
  pathname: string,
  activeChain: Chain,
  user: UserState['user']
) {
  // Strip chain prefix from pathname before matching against BASE_URLS
  const strippedPathname = stripChainFromPath(pathname);
  const navigableServices = getNavigableServices(user, activeChain);
  return navigableServices.find((s) =>
    strippedPathname.startsWith(BASE_URLS[s.id as keyof typeof BASE_URLS])
  )?.id;
}
